import React, { useCallback, useEffect, useState } from 'react'
import { Users, Plus, X, KeyRound, Trash2, Copy, CheckCircle, Shield } from 'lucide-react'
import { adminUsersAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const errMsg = (err, fallback) =>
  err.response?.data?.error?.message || err.response?.data?.message || err.message || fallback

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      role === 'owner' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
    }`}
  >
    {role === 'owner' ? 'Proprietário' : 'Equipe'}
  </span>
)

// Shown once after create / reset — the API never returns the password again.
const CredentialsNotice = ({ credentials, onClose }) => {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(credentials.temporary_password)
      setCopied(true)
    } catch {}
  }
  return (
    <div className="card p-4 border-l-4 border-l-green-500">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="font-medium text-admin-900 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            Credenciais enviadas por e-mail para {credentials.email}
          </p>
          <p className="text-sm text-admin-600">
            Senha temporária (exibida apenas uma vez — o usuário deverá alterá-la no primeiro acesso):
          </p>
          <div className="flex items-center gap-2">
            <code className="px-2 py-1 bg-admin-100 rounded text-sm">{credentials.temporary_password}</code>
            <button type="button" onClick={copy} className="text-admin-500 hover:text-admin-800" title="Copiar">
              {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <button type="button" onClick={onClose} className="text-admin-400 hover:text-admin-700" aria-label="Fechar">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

const CreateAdminUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ email: '', name: '', role: 'staff' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const res = await adminUsersAPI.create(form)
      onCreated(res.data)
    } catch (err) {
      setError(errMsg(err, 'Falha ao criar o usuário'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="card w-full max-w-md p-6 space-y-4 bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-admin-900">Novo usuário admin</h2>
          <button type="button" onClick={onClose} className="text-admin-400 hover:text-admin-700" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-admin-600">
          Uma senha temporária é gerada e enviada por e-mail ao usuário, que deverá alterá-la
          no primeiro acesso.
        </p>
        <div>
          <label htmlFor="admin-user-email" className="block text-sm font-medium text-admin-700 mb-1">E-mail</label>
          <input
            id="admin-user-email"
            type="email"
            className="input w-full"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="admin-user-name" className="block text-sm font-medium text-admin-700 mb-1">Nome</label>
          <input
            id="admin-user-name"
            type="text"
            className="input w-full"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="admin-user-role" className="block text-sm font-medium text-admin-700 mb-1">Função</label>
          <select
            id="admin-user-role"
            className="input w-full"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            <option value="staff">Equipe — ativar/desativar estúdios, suporte e depuração somente leitura</option>
            <option value="owner">Proprietário — acesso total</option>
          </select>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Criando...' : 'Criar e enviar e-mail'}
          </button>
        </div>
      </form>
    </div>
  )
}

export const AdminUsersPage = () => {
  const { user: me } = useAuth()
  const [users, setUsers] = useState([])
  const [builtinOwner, setBuiltinOwner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [credentials, setCredentials] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await adminUsersAPI.getAll()
      setUsers(res.data.data || [])
      setBuiltinOwner(res.data.builtin_owner || null)
    } catch (err) {
      setError(errMsg(err, 'Falha ao carregar os usuários admin'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const run = async (userId, action) => {
    setBusyId(userId)
    setError('')
    try {
      await action()
      await load()
    } catch (err) {
      setError(errMsg(err, 'Falha na ação'))
    } finally {
      setBusyId(null)
    }
  }

  const resetPassword = (u) => {
    if (!window.confirm(`Gerar uma nova senha temporária para ${u.email} e enviá-la por e-mail?`)) return
    run(u.id, async () => {
      const res = await adminUsersAPI.resetPassword(u.id)
      setCredentials(res.data.credentials)
    })
  }

  const toggleActive = (u) =>
    run(u.id, () => adminUsersAPI.update(u.id, { is_active: !u.is_active }))

  const changeRole = (u, role) => {
    if (role === u.role) return
    if (!window.confirm(`Alterar ${u.email} para ${role === 'owner' ? 'Proprietário (acesso total)' : 'Equipe'}?`)) return
    run(u.id, () => adminUsersAPI.update(u.id, { role }))
  }

  const remove = (u) => {
    if (!window.confirm(`Excluir ${u.email} permanentemente? Esta ação não pode ser desfeita.`)) return
    run(u.id, () => adminUsersAPI.remove(u.id))
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-primary-600" />
            Usuários admin
          </h1>
          <p className="text-sm text-admin-600 mt-1">
            Pessoas que podem entrar neste console. A equipe pode ativar/desativar estúdios e usar as
            telas de suporte e depuração somente leitura; todo o resto é exclusivo do proprietário.
          </p>
        </div>
        <button className="btn btn-primary flex items-center gap-1 whitespace-nowrap flex-shrink-0" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          Novo usuário
        </button>
      </div>

      {credentials && <CredentialsNotice credentials={credentials} onClose={() => setCredentials(null)} />}
      {error && <div className="card p-4 border-l-4 border-l-red-500 text-sm text-red-700">{error}</div>}

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-admin-50 text-left text-admin-600">
            <tr>
              <th className="px-4 py-3 font-medium">Usuário</th>
              <th className="px-4 py-3 font-medium">Função</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Último acesso</th>
              <th className="px-4 py-3 font-medium text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {builtinOwner && (
              <tr className="border-t border-admin-100">
                <td className="px-4 py-3">
                  <p className="font-medium text-admin-900 flex items-center gap-1">
                    <Shield className="h-4 w-4 text-purple-600" />
                    {builtinOwner.email}
                  </p>
                  <p className="text-xs text-admin-500">Conta integrada (credenciais do ambiente)</p>
                </td>
                <td className="px-4 py-3"><RoleBadge role="owner" /></td>
                <td className="px-4 py-3 text-admin-700">Ativo</td>
                <td className="px-4 py-3 text-admin-500">—</td>
                <td className="px-4 py-3" />
              </tr>
            )}
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-admin-500">Carregando...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-admin-500">Nenhum usuário admin ainda.</td>
              </tr>
            ) : (
              users.map((u) => {
                const isMe = me?.id === u.id
                const busy = busyId === u.id
                return (
                  <tr key={u.id} className="border-t border-admin-100">
                    <td className="px-4 py-3">
                      <p className="font-medium text-admin-900">
                        {u.name || u.email}
                        {isMe && <span className="ml-2 text-xs text-admin-500">(você)</span>}
                      </p>
                      {u.name && <p className="text-xs text-admin-500">{u.email}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {isMe ? (
                        <RoleBadge role={u.role} />
                      ) : (
                        <select
                          aria-label={`Função de ${u.email}`}
                          className="input py-1 text-sm min-w-[8.5rem]"
                          value={u.role}
                          disabled={busy}
                          onChange={(e) => changeRole(u, e.target.value)}
                        >
                          <option value="staff">Equipe</option>
                          <option value="owner">Proprietário</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={u.is_active ? 'text-green-700' : 'text-red-700'}>
                        {u.is_active ? 'Ativo' : 'Desativado'}
                      </span>
                      {u.must_change_password && (
                        <p className="text-xs text-amber-600">Troca de senha pendente</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-admin-600">{formatDate(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      {!isMe && (
                        <div className="flex justify-end gap-2">
                          <button
                            className="btn btn-secondary text-xs flex items-center gap-1 whitespace-nowrap"
                            disabled={busy}
                            onClick={() => resetPassword(u)}
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                            Redefinir senha
                          </button>
                          <button
                            className="btn btn-secondary text-xs"
                            disabled={busy}
                            onClick={() => toggleActive(u)}
                          >
                            {u.is_active ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            className="btn btn-secondary text-xs text-red-600"
                            disabled={busy}
                            onClick={() => remove(u)}
                            title="Excluir"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateAdminUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(data) => {
            setShowCreate(false)
            setCredentials(data.credentials)
            load()
          }}
        />
      )}
    </div>
  )
}
